import { Col, Container, Row, Stack } from 'react-bootstrap'
import React, { useMemo, useState } from 'react'
import { BlackDiamondSettingsModal } from './BlackDiamondSettingsModal'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { Link } from 'react-router-dom'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

const ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_SALESFORCE_ADMIN_CONSENT_LINK!

export type BlackDiamondIntegrationRowProps = { useIntegrationRedirectUri?: boolean }
export const BlackDiamondIntegrationRow: React.FC<BlackDiamondIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false)

  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()

  const hasBlackDiamondIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.BLACK_DIAMOND))
  }, [userData])

  const handleHideSettings = () => {
    setShowSettingsModal(false)
  }
  const handleShowSettingsModal = async () => {
    setShowSettingsModal(true)
  }

  const uninstallBlackDiamond = () => {
    const user = userData
    if (!user || !user.integrations) {
      return
    }
    const integration = user.integrations.find((i) => i.serviceId === IntegrationServiceId.BLACK_DIAMOND)
    if (integration) {
      deleteUserIntegration({
        accountId,
        payload: {
          userId: user.id,
          integrationId: integration.id,
        },
      })
    }
  }

  const authorizeBlackDiamond = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.BLACK_DIAMOND)
    const state = useIntegrationRedirectUri ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations') : createState()
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.BLACK_DIAMOND}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      {showSettingsModal && <BlackDiamondSettingsModal show={showSettingsModal} onHide={handleHideSettings} />}

      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.BLACK_DIAMOND} width={48} height={48} />
            </div>
            <div>
              <h6>SS&C BlackDiamond Edge</h6>
              <div>Connect your Zocks account to SS&C BlackDiamond Edge.</div>
              <div>
                Your Salesforce Admin must enable the Zocks app in Salesforce. For more info, click{' '}
                <Link className='zocks-text-gray-900 text-decoration-underline' to={ADMIN_CONSENT_LINK}>
                  here.
                </Link>{' '}
                &nbsp;&nbsp;
                <Tooltip
                  className='zocks-text-gray-500'
                  placement='right'
                  title='Go to Apps > Connected Apps > Connected Apps OAuth Usage and click Install next to Zocks before connecting.'
                >
                  <i className='bi bi-info-circle' />
                </Tooltip>
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            <div className='d-flex gap-3'>
              <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:read', superScope: ['account:admin'] }}>
                <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                  <i className='bi bi-gear' />
                </Button>
              </ScopedBlock>
            </div>
            <div className='connect-button-container'>
              {hasBlackDiamondIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallBlackDiamond} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={authorizeBlackDiamond}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
